import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getSignatureById, signDocument } from '../services/api';

export default function SignPage() {
  const { signatureId } = useParams<{ signatureId: string }>();
  const [sigRequest, setSigRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerIdNumber, setSignerIdNumber] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (!signatureId) return;
    getSignatureById(signatureId)
      .then(res => { setSigRequest(res.data); setLoading(false); })
      .catch(() => { setError('בקשת החתימה לא נמצאה'); setLoading(false); });
  }, [signatureId]);

  const getCanvasContext = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  };

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: (e as React.MouseEvent).clientX - rect.left,
      y: (e as React.MouseEvent).clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = getCanvasContext();
    if (!ctx) return;
    setIsDrawing(true);
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = getCanvasContext();
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!signatureId || !canvasRef.current) return;
    const signatureData = canvasRef.current.toDataURL('image/png');
    try {
      await signDocument(signatureId, { signatureData, signerName, signerIdNumber });
      setSigned(true);
    } catch {
      setError('החתימה נכשלה, אנא נסה שוב');
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Arial, sans-serif' }}>
      <p>טוען...</p>
    </div>
  );

  if (error && !sigRequest) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 16, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
        <h2>{error}</h2>
      </div>
    </div>
  );

  if (signed || sigRequest?.status === 'signed') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 16, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#4CAF50' }}>המסמך נחתם בהצלחה!</h2>
        <p style={{ color: '#666', marginTop: 8 }}>תודה רבה, החתימה נשמרה במערכת</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Arial, sans-serif', padding: '40px 16px' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#7B1FA2' }}>MANARA</div>
          <div style={{ fontSize: 14, color: '#999' }}>מכללת מנארה - חתימה דיגיטלית</div>
        </div>

        <div style={{ background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', marginBottom: 24 }}>
          <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>📄 מסמך לחתימה</h3>
          <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}><strong>מסמך:</strong> {sigRequest.documentName}</div>
            {sigRequest.message && <div style={{ marginBottom: 8 }}><strong>הודעה:</strong> {sigRequest.message}</div>}
            <div><strong>נשלח על ידי:</strong> {sigRequest.performedBy}</div>
          </div>

          {error && <div style={{ background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>}

          <h4 style={{ marginBottom: 12 }}>✍️ חתום כאן</h4>
          <div style={{ border: '2px solid #ddd', borderRadius: 8, marginBottom: 16, background: '#fff', touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              width={540}
              height={200}
              style={{ width: '100%', height: 200, cursor: 'crosshair' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <button onClick={clearCanvas} style={{ marginBottom: 16, padding: '6px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer' }}>
            🗑️ נקה חתימה
          </button>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>שם מלא</label>
            <input
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' }}
              value={signerName}
              onChange={e => setSignerName(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>מספר ת.ז.</label>
            <input
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 15, direction: 'ltr', textAlign: 'left', boxSizing: 'border-box' }}
              value={signerIdNumber}
              onChange={e => setSignerIdNumber(e.target.value)}
            />
          </div>

          <button
            onClick={handleSign}
            disabled={!hasSignature || !signerName || !signerIdNumber}
            style={{
              width: '100%', padding: '14px', borderRadius: 8, border: 'none',
              background: '#7B1FA2', color: '#fff', fontSize: 18, fontWeight: 700,
              cursor: 'pointer', opacity: (!hasSignature || !signerName || !signerIdNumber) ? 0.5 : 1
            }}
          >
            ✍️ חתום ואשר
          </button>
        </div>
      </div>
    </div>
  );
}
